import React, { useEffect, useMemo, useState, useCallback } from "react";
import AppShell from "@/components/ascend/AppShell";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  isoDate, parseIsoDate, weekStartOf, addDays, dayLabel, dayLong,
  formatWeekRange, todayIsoDate, todayDayOfWeek, slotsForDay,
  SLOT_STUDY, SLOT_BREAK, SLOT_DINNER, SLOT_WIND, isMissed,
} from "@/lib/time";
import { ChevronLeft, ChevronRight, Plus, Play, Check, Trash2, X, Coffee, UtensilsCrossed, Moon, Sparkles } from "lucide-react";
import FocusModal from "@/components/ascend/FocusModal";
import { useAuth } from "@/context/AuthContext";
import { textColorForSubject } from "@/lib/subjectColor";

export default function Timetable() {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(() => weekStartOf(new Date()));
  const [subjects, setSubjects] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null); // { day, start } for empty slot popover
  const [selectedBlock, setSelectedBlock] = useState(null); // filled block popover
  const [dragBlockId, setDragBlockId] = useState(null);
  const [focusSession, setFocusSession] = useState(null); // { session_id, block_id, subject_id, started_at, subject_name }
  const [northStar, setNorthStar] = useState("");

  const weekStartStr = useMemo(() => isoDate(weekStart), [weekStart]);

  const subjectById = useMemo(() => {
    const m = new Map();
    subjects.forEach((s) => m.set(s.id, s));
    return m;
  }, [subjects]);

  const blocksByKey = useMemo(() => {
    const m = new Map();
    blocks.forEach((b) => m.set(`${b.day_of_week}|${b.start_time}`, b));
    return m;
  }, [blocks]);

  const loadWeek = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/timetable?week_start=${weekStartStr}`);
      setBlocks(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [weekStartStr]);

  useEffect(() => {
    (async () => {
      try {
        const [s, d] = await Promise.all([api.get("/subjects"), api.get("/me/dream")]);
        setSubjects(s.data.items || []);
        setNorthStar(d.data?.north_star_short || "");
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  useEffect(() => { loadWeek(); }, [loadWeek]);

  const goPrev = () => setWeekStart(addDays(weekStart, -7));
  const goNext = () => setWeekStart(addDays(weekStart, 7));
  const goToday = () => setWeekStart(weekStartOf(new Date()));

  const isThisWeek = useMemo(() => weekStartStr === isoDate(weekStartOf(new Date())), [weekStartStr]);

  const assign = async (subjectId, note) => {
    if (!selectedSlot) return;
    try {
      const { data } = await api.post("/timetable/blocks", {
        subject_id: subjectId,
        week_start_date: weekStartStr,
        day_of_week: selectedSlot.day,
        start_time: selectedSlot.start,
        note: note || null,
      });
      setBlocks((arr) => [...arr, data]);
      setSelectedSlot(null);
      toast.success("Block added.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveBlockEdit = async (patch) => {
    if (!selectedBlock) return;
    try {
      const { data } = await api.patch(`/timetable/blocks/${selectedBlock.id}`, patch);
      setBlocks((arr) => arr.map((b) => (b.id === data.id ? data : b)));
      setSelectedBlock(null);
      toast.success("Saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const clearBlock = async (block) => {
    try {
      await api.delete(`/timetable/blocks/${block.id}`);
      setBlocks((arr) => arr.filter((b) => b.id !== block.id));
      setSelectedBlock(null);
      toast.success("Slot cleared.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const startFocus = async (block) => {
    try {
      const { data } = await api.post("/focus/start", { block_id: block.id });
      const subj = subjectById.get(String(block.subject_id));
      setFocusSession({ ...data, subject_name: subj?.name || "Study", subject_color: subj?.color || "#999" });
      setSelectedBlock(null);
      // reflect status
      setBlocks((arr) => arr.map((b) => (b.id === block.id ? { ...b, status: "in_progress" } : b)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const manualComplete = async (block) => {
    try {
      await api.post(`/timetable/blocks/${block.id}/complete`);
      setBlocks((arr) => arr.map((b) => (b.id === block.id ? { ...b, status: "completed", completed_at: new Date().toISOString() } : b)));
      setSelectedBlock(null);
      toast.success("Logged — 45 minutes.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onFocusFinished = (result) => {
    if (result?.block_id) {
      if (result.completed) {
        setBlocks((arr) => arr.map((b) => (b.id === result.block_id ? { ...b, status: "completed", completed_at: new Date().toISOString() } : b)));
      } else {
        setBlocks((arr) => arr.map((b) => (b.id === result.block_id ? { ...b, status: "scheduled" } : b)));
      }
    }
    setFocusSession(null);
  };

  // Drag & drop
  const onDragStart = (e, block) => {
    setDragBlockId(block.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", block.id);
  };
  const onDrop = async (e, day, start) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || dragBlockId;
    setDragBlockId(null);
    if (!id) return;
    // If target already has a block, no-op
    if (blocksByKey.get(`${day}|${start}`)) {
      toast.error("Slot already has a block.");
      return;
    }
    try {
      const { data } = await api.patch(`/timetable/blocks/${id}`, {
        day_of_week: day, start_time: start, week_start_date: weekStartStr,
      });
      setBlocks((arr) => arr.map((b) => (b.id === data.id ? data : b)));
      toast.success("Moved.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const allowDrop = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  return (
    <AppShell>
      <Toaster richColors position="top-center" />

      <div className="pt-2 flex flex-wrap items-end justify-between gap-3 mb-6 fade-up">
        <div>
          <span className="eyebrow">your week</span>
          <h1 className="font-display text-4xl sm:text-5xl mt-1" style={{ color: "var(--text)" }}>
            Timetable
          </h1>
          <p className="text-muted mt-1">Assign study blocks. Breaks and dinner keep you human.</p>
        </div>
        <div className="flex items-center gap-2" data-testid="week-nav">
          <button className="btn-ghost !px-3 !py-2" onClick={goPrev} data-testid="week-prev-btn" aria-label="Previous week">
            <ChevronLeft size={16} strokeWidth={1.5} />
          </button>
          <div className="px-4 py-2 rounded-full text-sm font-medium card-soft-2" data-testid="week-range-label">
            {formatWeekRange(weekStart)}
          </div>
          <button className="btn-ghost !px-3 !py-2" onClick={goNext} data-testid="week-next-btn" aria-label="Next week">
            <ChevronRight size={16} strokeWidth={1.5} />
          </button>
          {!isThisWeek && (
            <button className="btn-ghost text-sm" onClick={goToday} data-testid="week-today-btn">
              This week
            </button>
          )}
        </div>
      </div>

      {/* Desktop grid */}
      <div className="hidden md:block" data-testid="timetable-grid-desktop">
        <div className="grid grid-cols-7 gap-2">
          {Array.from({ length: 7 }).map((_, day) => {
            const d = addDays(weekStart, day);
            const isToday = isoDate(d) === todayIsoDate();
            return (
              <div key={day} className="text-center py-2 fade-up">
                <div className="text-xs uppercase tracking-wider text-muted">{dayLabel(day)}</div>
                <div className={`font-display text-2xl ${isToday ? "font-semibold" : ""}`}
                     style={{ color: isToday ? "var(--primary)" : "var(--text)" }}>
                  {d.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-2 grid grid-cols-7 gap-2 items-start">
          {Array.from({ length: 7 }).map((_, day) => (
            <DayColumn
              key={day}
              day={day}
              blocks={blocksByKey}
              subjectById={subjectById}
              weekStartStr={weekStartStr}
              onEmpty={(start) => setSelectedSlot({ day, start })}
              onBlock={(block) => setSelectedBlock(block)}
              onDragStart={onDragStart}
              onDrop={onDrop}
              allowDrop={allowDrop}
            />
          ))}
        </div>
      </div>

      {/* Mobile: day-by-day */}
      <div className="md:hidden space-y-6" data-testid="timetable-mobile">
        {Array.from({ length: 7 }).map((_, day) => {
          const d = addDays(weekStart, day);
          const isToday = isoDate(d) === todayIsoDate();
          return (
            <div key={day} className="card-soft p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="font-display text-2xl" style={{ color: isToday ? "var(--primary)" : "var(--text)" }}>
                  {dayLong(day)} <span className="text-muted text-lg">{d.getDate()}</span>
                </div>
                {isToday && <span className="font-hand text-base" style={{ color: "var(--accent)" }}>today</span>}
              </div>
              <DayColumn
                mobile
                day={day}
                blocks={blocksByKey}
                subjectById={subjectById}
                weekStartStr={weekStartStr}
                onEmpty={(start) => setSelectedSlot({ day, start })}
                onBlock={(block) => setSelectedBlock(block)}
                onDragStart={onDragStart}
                onDrop={onDrop}
                allowDrop={allowDrop}
              />
            </div>
          );
        })}
      </div>

      {loading && <div className="text-sm text-muted mt-4">Loading week…</div>}

      {/* Assign popover */}
      {selectedSlot && (
        <Modal onClose={() => setSelectedSlot(null)} testid="assign-modal">
          <div className="mb-4">
            <span className="eyebrow">assign study block</span>
            <h3 className="font-display text-3xl mt-1" style={{ color: "var(--text)" }}>
              {dayLong(selectedSlot.day)}, {selectedSlot.start}
            </h3>
          </div>
          <AssignForm subjects={subjects} onSave={assign} />
        </Modal>
      )}

      {/* Edit popover */}
      {selectedBlock && (
        <Modal onClose={() => setSelectedBlock(null)} testid="edit-block-modal">
          <BlockEditor
            block={selectedBlock}
            subjects={subjects}
            subject={subjectById.get(String(selectedBlock.subject_id))}
            onSave={saveBlockEdit}
            onClear={() => clearBlock(selectedBlock)}
            onStart={() => startFocus(selectedBlock)}
            onManualComplete={() => manualComplete(selectedBlock)}
          />
        </Modal>
      )}

      {/* Focus overlay */}
      {focusSession && (
        <FocusModal session={focusSession} northStar={northStar} onFinish={onFocusFinished} />
      )}
    </AppShell>
  );
}

function TimeRail() {
  // Render times inside DayColumn — this rail is a spacer so grid aligns nicely.
  const slots = slotsForDay(0); // any weekday
  return (
    <div className="flex flex-col gap-1 pt-1">
      {slots.map((s, i) => (
        <div key={i} className="text-[11px] text-muted text-right pr-2"
             style={{ minHeight: slotHeight(s.type) }}>
          {s.start}
        </div>
      ))}
    </div>
  );
}

function slotHeight(type) {
  if (type === SLOT_STUDY) return 76;
  if (type === SLOT_DINNER) return 44;
  if (type === SLOT_WIND) return 30;
  return 24; // break
}

function DayColumn({ day, blocks, subjectById, weekStartStr, onEmpty, onBlock, onDragStart, onDrop, allowDrop, mobile = false }) {
  const slots = slotsForDay(day);
  return (
    <div className={`flex flex-col gap-1 ${mobile ? "" : "pt-1"}`}>
      {slots.map((s, idx) => {
        const key = `${day}|${s.start}`;
        const block = s.type === SLOT_STUDY ? blocks.get(key) : null;
        const subj = block ? subjectById.get(String(block.subject_id)) : null;

        if (s.type === SLOT_BREAK) {
          return <BreakPill key={idx} label={mobile ? `${s.start} – ${s.end} · 15m break` : "15m"} />;
        }
        if (s.type === SLOT_DINNER) {
          return <DinnerPill key={idx} label={mobile ? `${s.start} – ${s.end} · Dinner` : "Dinner"} />;
        }
        if (s.type === SLOT_WIND) {
          return <WindPill key={idx} label={mobile ? `${s.start} – ${s.end} · wind-down` : "wind-down"} />;
        }
        // study
        if (!block) {
          return (
            <EmptySlot
              key={idx}
              mobile={mobile}
              start={s.start}
              end={s.end}
              onClick={() => onEmpty(s.start)}
              onDrop={(e) => onDrop(e, day, s.start)}
              allowDrop={allowDrop}
              testid={`slot-empty-${day}-${s.start.replace(":","-")}`}
            />
          );
        }
        return (
          <FilledBlock
            key={idx}
            mobile={mobile}
            block={block}
            subject={subj}
            missed={isMissed(block, weekStartStr)}
            onClick={() => onBlock(block)}
            onDragStart={(e) => onDragStart(e, block)}
            testid={`block-${day}-${s.start.replace(":","-")}`}
          />
        );
      })}
    </div>
  );
}

function BreakPill({ label }) {
  return (
    <div
      className="flex items-center gap-1 justify-center text-[10px] font-medium rounded-full"
      style={{
        minHeight: slotHeight(SLOT_BREAK),
        background: "color-mix(in oklab, var(--primary-soft) 45%, transparent)",
        color: "var(--text-muted)",
      }}
    >
      <Coffee size={11} strokeWidth={1.5} /> {label}
    </div>
  );
}
function DinnerPill({ label }) {
  return (
    <div
      className="flex items-center gap-1.5 justify-center text-[11px] font-medium rounded-full"
      style={{
        minHeight: slotHeight(SLOT_DINNER),
        background: "color-mix(in oklab, var(--primary) 14%, var(--surface))",
        color: "var(--accent)",
        border: "1px solid var(--border)",
      }}
      data-testid="slot-dinner"
    >
      <UtensilsCrossed size={12} strokeWidth={1.5} /> {label}
    </div>
  );
}
function WindPill({ label }) {
  return (
    <div
      className="flex items-center gap-1 justify-center text-[10px] rounded-full"
      style={{
        minHeight: slotHeight(SLOT_WIND),
        background: "transparent",
        color: "var(--text-muted)",
        border: "1px dashed var(--border)",
      }}
    >
      <Moon size={10} strokeWidth={1.5} /> {label}
    </div>
  );
}

function EmptySlot({ start, end, onClick, onDrop, allowDrop, testid, mobile }) {
  return (
    <button
      type="button"
      onClick={onClick}
      onDrop={onDrop}
      onDragOver={allowDrop}
      data-testid={testid}
      className="rounded-2xl text-xs flex flex-col items-center justify-center text-muted transition-all"
      style={{
        minHeight: slotHeight(SLOT_STUDY),
        border: "1.5px dashed var(--border)",
        background: "var(--surface-2)",
      }}
    >
      <span className="text-[10px] font-medium tracking-wide">{start}</span>
      <Plus size={14} strokeWidth={1.5} className="mt-0.5" />
    </button>
  );
}

function FilledBlock({ block, subject, onClick, onDragStart, testid, missed, mobile }) {
  const color = subject?.color || "#888";
  const isDone = block.status === "completed";
  const isProgress = block.status === "in_progress";
  const nameColor = missed ? "var(--text-muted)" : textColorForSubject(color);
  return (
    <button
      type="button"
      onClick={onClick}
      draggable={!isDone}
      onDragStart={onDragStart}
      data-testid={testid}
      className="relative rounded-2xl pl-4 pr-3 py-2 text-left transition-all cursor-grab active:cursor-grabbing overflow-hidden"
      style={{
        minHeight: slotHeight(SLOT_STUDY),
        background: missed
          ? "var(--surface-2)"
          : isDone
            ? `color-mix(in oklab, ${color} 26%, var(--surface))`
            : `color-mix(in oklab, ${color} 18%, var(--surface))`,
        border: `1px solid ${missed ? "var(--border)" : `color-mix(in oklab, ${color} 45%, transparent)`}`,
        opacity: missed ? 0.75 : 1,
      }}
    >
      {/* Left accent stripe — subject color at full opacity */}
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0 rounded-l-2xl"
        style={{ width: 4, background: missed ? "var(--border)" : color }}
      />
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-medium tabular-nums" style={{ color: "var(--text-muted)" }}>{block.start_time}</span>
      </div>
      <div className="font-display text-base leading-tight truncate mt-0.5" style={{ color: nameColor }}>
        {subject?.name || "Study"}
      </div>
      <div className="text-[10px] mt-0.5 flex items-center gap-1 flex-wrap" style={{ color: "var(--text-muted)" }}>
        {isDone && <span className="px-1.5 rounded-full" style={{ background: color, color: "#fff" }}>done</span>}
        {isProgress && <span className="px-1.5 rounded-full" style={{ background: "var(--primary)", color: "#fff" }}>in focus</span>}
        {missed && !isDone && <span className="px-1.5 rounded-full" style={{ background: "var(--border)", color: "var(--text-muted)" }}>missed</span>}
        {block.note && <span className="truncate italic">"{block.note}"</span>}
      </div>
    </button>
  );
}

function Modal({ children, onClose, testid }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(20, 15, 22, 0.35)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid={testid}
    >
      <div
        className="card-soft p-6 sm:p-8 max-w-md w-full fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-[var(--surface-2)]"
          data-testid="modal-close-btn"
          aria-label="Close"
          style={{ position: "relative", float: "right", marginTop: "-8px", marginRight: "-8px" }}
        >
          <X size={16} strokeWidth={1.5} />
        </button>
        <div className="clear-both" />
        {children}
      </div>
    </div>
  );
}

function AssignForm({ subjects, onSave }) {
  const [subjectId, setSubjectId] = useState(subjects[0]?.id || "");
  const [note, setNote] = useState("");
  useEffect(() => { if (!subjectId && subjects[0]) setSubjectId(subjects[0].id); }, [subjects, subjectId]);
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (subjectId) onSave(subjectId, note); }}
      className="space-y-4"
    >
      <div>
        <label className="text-xs uppercase tracking-wider text-muted mb-2 block">Subject</label>
        <div className="grid grid-cols-2 gap-2">
          {subjects.map((s) => (
            <button
              type="button"
              key={s.id}
              onClick={() => setSubjectId(s.id)}
              data-testid={`assign-subject-${s.name.toLowerCase()}`}
              className="flex items-center gap-2 px-3 py-2 rounded-full text-sm transition-all"
              style={{
                background: subjectId === s.id ? `color-mix(in oklab, ${s.color} 22%, var(--surface))` : "var(--surface-2)",
                border: `1px solid ${subjectId === s.id ? s.color : "var(--border)"}`,
                color: "var(--text)",
              }}
            >
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
              {s.name}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wider text-muted mb-1 block">Note (optional)</label>
        <input
          className="input-soft"
          placeholder="e.g. chapter 4 exercises"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={280}
          data-testid="assign-note-input"
        />
      </div>
      <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" data-testid="assign-save-btn">
        <Sparkles size={16} strokeWidth={1.5} /> Add block
      </button>
    </form>
  );
}

function BlockEditor({ block, subjects, subject, onSave, onClear, onStart, onManualComplete }) {
  const [subjectId, setSubjectId] = useState(block.subject_id);
  const [note, setNote] = useState(block.note || "");
  const isDone = block.status === "completed";

  return (
    <div>
      <div className="mb-4">
        <span className="eyebrow">study block</span>
        <h3 className="font-display text-3xl mt-1" style={{ color: "var(--text)" }}>
          {block.start_time}
        </h3>
        <div className="text-sm text-muted">
          {subject?.name || "—"} · 45 minutes · <span className="font-medium" style={{ color: isDone ? "var(--primary)" : "var(--accent)" }}>{block.status}</span>
        </div>
      </div>

      {!isDone && (
        <div className="flex flex-col sm:flex-row gap-2 mb-5">
          <button className="btn-primary flex-1 flex items-center justify-center gap-2" onClick={onStart} data-testid="block-start-focus-btn">
            <Play size={14} strokeWidth={1.75} /> Start focus (45m)
          </button>
          <button className="btn-ghost flex-1 flex items-center justify-center gap-2" onClick={onManualComplete} data-testid="block-manual-complete-btn">
            <Check size={14} strokeWidth={1.75} /> Mark done
          </button>
        </div>
      )}

      <div className="space-y-3 mb-5">
        <div>
          <label className="text-xs uppercase tracking-wider text-muted mb-2 block">Change subject</label>
          <div className="grid grid-cols-2 gap-2">
            {subjects.map((s) => (
              <button
                type="button"
                key={s.id}
                onClick={() => setSubjectId(s.id)}
                data-testid={`edit-subject-${s.name.toLowerCase()}`}
                className="flex items-center gap-2 px-3 py-2 rounded-full text-sm"
                style={{
                  background: subjectId === s.id ? `color-mix(in oklab, ${s.color} 22%, var(--surface))` : "var(--surface-2)",
                  border: `1px solid ${subjectId === s.id ? s.color : "var(--border)"}`,
                  color: "var(--text)",
                }}
              >
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} /> {s.name}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-muted mb-1 block">Note</label>
          <input
            className="input-soft"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={280}
            data-testid="edit-note-input"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          className="btn-primary flex-1 flex items-center justify-center gap-2"
          onClick={() => onSave({ subject_id: subjectId, note })}
          data-testid="block-save-btn"
        >
          Save changes
        </button>
        <button
          className="btn-ghost flex items-center justify-center gap-2 text-sm"
          onClick={onClear}
          data-testid="block-clear-btn"
        >
          <Trash2 size={14} strokeWidth={1.5} /> Clear slot
        </button>
      </div>
    </div>
  );
}
